import {
	AvailablePayInThreeAmountStateEnum,
	BillingDefermentStateEnum,
	BillingDefermentUnavailableStateReasonEnum,
	MarketingCampaignEnum,
	PaymentAvailabilityEnum,
	PaymentAvailabilityMethodsEnum,
	PaymentAvailabilityReasonEnum,
	TransferBonusTypeEnum,
} from '.spec/self-care/tsTypes';
import { format } from 'date-fns';
import type { PagingInfo } from 'types/pagingInfo';

import type { Amount } from '@skippay/core/@types/amount';
import type { MyContract } from '@skippay/core/@types/myContract';
import { toFloat } from '@skippay/core/utils/amountUtils';
import type { Paginated } from '@skippay/core/utils/queryUtils';
import type { Maybe } from '@skippay/core/utils/types';

import { toIsoDate } from 'jarabot/utils';
import { isBillingPayable } from 'utils/billingUtils';

import JarabotBillingQuery from '../../queries/JarabotBillingQuery.graphql';
import JarabotContractQuery from '../../queries/JarabotContractQuery.graphql';
import {
	BillingCashback,
	BillingDeferment,
	BillingInfo,
	BillingInstallments,
	BillingTransferBonus,
	LimitInfo,
	RevolvingInfo,
} from '../../types';
import type { DomainSource } from '../types';

interface ContractData {
	myContract?: {
		activeProducts?: MyContract['activeProducts'];
		availableAmount?: MyContract['availableAmount'];
		totalLimit?: MyContract['totalLimit'];
		availableAmountState?: MyContract['availableAmountState'];
		availableAnnualAmount?: MyContract['availableAnnualAmount'];
		totalAnnualLimit?: MyContract['totalAnnualLimit'];
		limitAmendmentEntryState?: MyContract['limitAmendmentEntryState'];
		limitAmendmentMaxAllowedEntryLimit?: MyContract['limitAmendmentMaxAllowedEntryLimit'];
		preAmendmentLimitIncreaseOffer?: MyContract['preAmendmentLimitIncreaseOffer'];
		payInThreeAdditionalInfo?: Maybe<{
			availableAmount?: Maybe<Amount>;
			availableAmountState?: Maybe<AvailablePayInThreeAmountStateEnum>;
			minimalPurchaseAmount?: Maybe<Amount>;
			totalLimit?: Maybe<Amount>;
		}>;
		revolvingActivatedDatetime?: Maybe<MyContract['revolvingActivatedDatetime']>;
		revolvingLoanContractAdditionalInfo?: MyContract['revolvingLoanContractAdditionalInfo'];
	} | null;
}

export const contractSource: DomainSource<ContractData> = {
	domains: ['limit', 'revolving', 'account'],
	query: JarabotContractQuery,
	toModel: (data) => {
		const contract = data.myContract;
		const payInThree = contract?.payInThreeAdditionalInfo;
		const limit: LimitInfo | undefined = contract
			? {
					available: contract.availableAmount ? toFloat(contract.availableAmount) : undefined,
					total: contract.totalLimit ? toFloat(contract.totalLimit) : undefined,
					state: contract.availableAmountState ?? undefined,
					annualAvailable: contract.availableAnnualAmount
						? toFloat(contract.availableAnnualAmount)
						: undefined,
					annualTotal: contract.totalAnnualLimit ? toFloat(contract.totalAnnualLimit) : undefined,
					amendmentState: contract.limitAmendmentEntryState ?? undefined,
					amendmentMaxAllowed: contract.limitAmendmentMaxAllowedEntryLimit
						? toFloat(contract.limitAmendmentMaxAllowedEntryLimit)
						: undefined,
					increaseOfferAvailable:
						contract.preAmendmentLimitIncreaseOffer?.limitIncreaseAvailable ?? undefined,
					payInThree: payInThree
						? {
								available: payInThree.availableAmount
									? toFloat(payInThree.availableAmount)
									: undefined,
								total: payInThree.totalLimit ? toFloat(payInThree.totalLimit) : undefined,
								minimalPurchase: payInThree.minimalPurchaseAmount
									? toFloat(payInThree.minimalPurchaseAmount)
									: undefined,
								state: payInThree.availableAmountState ?? undefined,
							}
						: undefined,
				}
			: undefined;
		const info = contract?.revolvingLoanContractAdditionalInfo;
		const revolving: RevolvingInfo | undefined =
			info || contract?.revolvingActivatedDatetime
				? {
						state: info?.state ?? undefined,
						applicationState: info?.applicationState ?? undefined,
						activatedDate: toIsoDate(contract?.revolvingActivatedDatetime),
					}
				: undefined;
		return {
			limit,
			revolving,
			activeProducts: contract?.activeProducts ?? undefined,
		};
	},
};

interface BillingRow {
	id?: Maybe<string>;
	balance?: Maybe<Amount>;
	totalAmountInvested?: Maybe<Amount>;
	dueDate?: Maybe<string>;
	paymentDate?: Maybe<string>;
	currentDpd?: Maybe<number>;
	isPaid?: Maybe<boolean>;
	month?: Maybe<number>;
	year?: Maybe<number>;
	coinReward?: Maybe<Amount>;
	campaigns?: Maybe<Array<{ slug?: Maybe<MarketingCampaignEnum> }>>;
	paymentAvailability?: Maybe<{
		availability?: Maybe<PaymentAvailabilityEnum>;
		methodSlugs?: Maybe<PaymentAvailabilityMethodsEnum[]>;
		reasons?: Maybe<PaymentAvailabilityReasonEnum[]>;
	}>;
	additionalInfoCashback?: Maybe<{
		availableAmount?: Maybe<Amount>;
		appliedAmount?: Maybe<Amount>;
		isApplicable?: Maybe<boolean>;
		isApplied?: Maybe<boolean>;
	}>;
	additionalInfoDeferment?: Maybe<{
		minimumToPay?: Maybe<Amount>;
		state?: Maybe<BillingDefermentStateEnum>;
		stateReason?: Maybe<BillingDefermentUnavailableStateReasonEnum>;
		deferment?: Maybe<{
			fee?: Maybe<Amount>;
			feeDiscount?: Maybe<Amount>;
			amountToDefer?: Maybe<Amount>;
			totalValue?: Maybe<Amount>;
			billingDefermentCount?: Maybe<number>;
		}>;
	}>;
	additionalInfoInstallments?: Maybe<{
		canApplyForInstallments?: Maybe<boolean>;
		maxInstallmentsPrincipal?: Maybe<Amount>;
	}>;
	additionalInfoTransferBonus?: Maybe<{
		type?: Maybe<TransferBonusTypeEnum>;
		amount?: Maybe<Amount>;
	}>;
}

interface BillingData {
	currentBilling?: Paginated<BillingRow>;
	oldestUnpaidBilling?: Paginated<BillingRow> & {
		pagingInfo?: Maybe<Pick<PagingInfo, 'totalCount'>>;
	};
}

const toBillingPeriod = (row: BillingRow): string | undefined =>
	row.year != null && row.month != null
		? format(new Date(row.year, row.month), 'yyyy-MM')
		: undefined;

const toDeferment = (row: BillingRow): BillingDeferment | undefined => {
	const deferment = row.additionalInfoDeferment?.deferment;
	if (!deferment) return undefined;
	return {
		fee: deferment.fee ? toFloat(deferment.fee) : undefined,
		feeDiscount: deferment.feeDiscount ? toFloat(deferment.feeDiscount) : undefined,
		amountToDefer: deferment.amountToDefer ? toFloat(deferment.amountToDefer) : undefined,
		totalValue: deferment.totalValue ? toFloat(deferment.totalValue) : undefined,
		count: deferment.billingDefermentCount ?? undefined,
	};
};

const toCashback = (row: BillingRow): BillingCashback | undefined => {
	const cashback = row.additionalInfoCashback;
	if (!cashback) return undefined;
	return {
		available: cashback.availableAmount ? toFloat(cashback.availableAmount) : undefined,
		applied: cashback.appliedAmount ? toFloat(cashback.appliedAmount) : undefined,
		isApplicable: cashback.isApplicable ?? undefined,
		isApplied: cashback.isApplied ?? undefined,
	};
};

const toSplitToInstallments = (row: BillingRow): BillingInstallments | undefined => {
	const installments = row.additionalInfoInstallments;
	if (!installments) return undefined;
	return {
		canApply: installments.canApplyForInstallments ?? undefined,
		maxPrincipal: installments.maxInstallmentsPrincipal
			? toFloat(installments.maxInstallmentsPrincipal)
			: undefined,
	};
};

const toTransferBonus = (row: BillingRow): BillingTransferBonus | undefined => {
	const bonus = row.additionalInfoTransferBonus;
	if (!bonus) return undefined;
	return {
		type: bonus.type ?? undefined,
		amount: bonus.amount ? toFloat(bonus.amount) : undefined,
	};
};

const toBillingInfo = (row: BillingRow, unpaidCount?: Maybe<number>): BillingInfo => {
	const deferment = row.additionalInfoDeferment;
	const availability = row.paymentAvailability;
	return {
		payable: row.id && row.balance ? { id: row.id, value: row.balance } : undefined,
		balance: row.balance ? toFloat(row.balance) : undefined,
		total: row.totalAmountInvested ? toFloat(row.totalAmountInvested) : undefined,
		minimumToPay: deferment?.minimumToPay ? toFloat(deferment.minimumToPay) : undefined,
		dueDate: toIsoDate(row.dueDate),
		daysOverdue: row.currentDpd ?? undefined,
		isPaid: row.isPaid ?? undefined,
		canPay: isBillingPayable(row),
		paymentMethods: availability?.methodSlugs ?? undefined,
		canDefer: deferment?.state === BillingDefermentStateEnum.AVAILABLE,
		period: toBillingPeriod(row),
		paymentDate: toIsoDate(row.paymentDate),
		whyCannotPay:
			availability?.availability === PaymentAvailabilityEnum.NOT_AVAILABLE
				? (availability.reasons ?? undefined)
				: undefined,
		deferment: toDeferment(row),
		defermentUnavailableReason: deferment?.stateReason ?? undefined,
		cashback: toCashback(row),
		splitToInstallments: toSplitToInstallments(row),
		transferBonus: toTransferBonus(row),
		coinReward: row.coinReward ? toFloat(row.coinReward) : undefined,
		campaigns: row.campaigns
			? row.campaigns
					.map((campaign) => campaign.slug)
					.filter((slug): slug is MarketingCampaignEnum => !!slug)
			: undefined,
		unpaidCount: unpaidCount ?? undefined,
	};
};

export const billingSource: DomainSource<BillingData> = {
	domains: ['billing', 'unpaidBilling'],
	query: JarabotBillingQuery,
	toModel: (data) => {
		const current = data.currentBilling?.data?.[0];
		const oldestUnpaid = data.oldestUnpaidBilling?.data?.[0];
		const unpaid = oldestUnpaid && oldestUnpaid.id !== current?.id ? oldestUnpaid : undefined;
		return {
			billing: current
				? toBillingInfo(current, data.oldestUnpaidBilling?.pagingInfo?.totalCount)
				: undefined,
			unpaidBilling: unpaid ? toBillingInfo(unpaid) : undefined,
		};
	},
};
